import Vue from 'vue'

// v-inline-files
//
// Le mail ricevute (helpdesk, mail log) arrivano dal backend con le immagini
// inline gia' riscritte da `src="cid:..."` a `src="/files/<path>"` (MailSyncService,
// task #7320). Quell'URL pero' e' relativo: nel browser si risolve sull'host della
// SPA (che risponde con la index.html) e comunque la rotta API `files/{path}` sta
// dietro `auth:api`, che un tag <img> non puo' soddisfare. Risultato: immagine rotta,
// e non piu' nemmeno tra gli allegati.
//
// La direttiva, applicata al contenitore renderizzato con v-html, intercetta ogni
// <img src="/files/..."> e lo carica come fanno gia' FilePreviewDialog e getFile():
// GET autenticata via axios (baseURL del tenant + Bearer) -> blob -> object URL.
// Il src originale viene rimosso subito, cosi' il browser non tenta la richiesta
// verso l'host sbagliato. Gli object URL vengono revocati quando l'img sparisce dal
// contenitore o alla distruzione dell'elemento.
//
// Il path nel src e' gia' URL-encoded dal backend (MailSyncService::filesUrl): va
// usato cosi' com'e', senza ri-codificarlo.

const SRC_ATTR = 'data-inline-files-src'
const STATE_ATTR = 'data-inline-files-state'
const FILES_PREFIX = '/files/'

// el -> Map<img, objectUrl>
const registry = new WeakMap()

function urlsFor(el) {
	let map = registry.get(el)
	if (!map) {
		map = new Map()
		registry.set(el, map)
	}
	return map
}

function revokeDetached(el) {
	const map = registry.get(el)
	if (!map) return
	map.forEach((url, img) => {
		if (!el.contains(img)) {
			window.URL.revokeObjectURL(url)
			map.delete(img)
		}
	})
}

function revokeAll(el) {
	const map = registry.get(el)
	if (!map) return
	map.forEach(url => window.URL.revokeObjectURL(url))
	map.clear()
	registry.delete(el)
}

// Il src arriva dal mittente della mail: prima di spenderci una GET autenticata lo si
// normalizza (il parser URL risolve i segmenti `..`, anche percent-encoded) e si accetta
// solo un pathname che resti sotto /files/. Query e fragment vengono rifiutati: il
// backend codifica il path (MailSyncService::filesUrl), quindi `?` e `#` letterali non
// possono comparire in un riferimento legittimo.
function safeFilesPath(src) {
	if (!src.startsWith(FILES_PREFIX)) return null
	let url
	try {
		url = new URL(src, 'http://inline-files.invalid')
	} catch (e) {
		return null
	}
	if (url.search || url.hash) return null
	const pathname = url.pathname
	if (!pathname.startsWith(FILES_PREFIX)) return null
	// Segmenti valutati anche dopo il percent-decoding: `..%2fy` non deve passare.
	const badSegment = pathname.split('/').some(seg => {
		let decoded
		try {
			decoded = decodeURIComponent(seg)
		} catch (e) {
			return true
		}
		return decoded === '..' || decoded === '.' || decoded.includes('/') || decoded.includes('\\')
	})
	return badSegment ? null : pathname
}

function resolveImage(el, img) {
	const src = img.getAttribute('src') || ''
	if (!src.startsWith(FILES_PREFIX)) return

	// Il src va neutralizzato in ogni caso: anche un path rifiutato non deve finire
	// in una richiesta verso l'host della SPA, ne' essere riesaminato a ogni update.
	img.setAttribute(SRC_ATTR, src)
	img.removeAttribute('src')

	const path = safeFilesPath(src)
	if (path === null) {
		console.warn('[inline-files] path immagine rifiutato:', src)
		img.setAttribute(STATE_ATTR, 'rejected')
		if (!img.getAttribute('title')) {
			img.setAttribute('title', 'Immagine non disponibile')
		}
		return
	}

	img.setAttribute(STATE_ATTR, 'loading')

	Vue.axios.get(path, { responseType: 'blob', headers: { Accept: '*/*' } })
		.then(resp => {
			// La GET puo' finire dopo l'unbind (registry senza entry: revokeAll l'ha tolta)
			// o dopo un re-render di v-html che ha rimosso l'img: in entrambi i casi non
			// si crea un object URL che nessuno revocherebbe.
			const map = registry.get(el)
			if (!map || !el.contains(img)) return
			const url = window.URL.createObjectURL(resp.data)
			map.set(img, url)
			img.setAttribute('src', url)
			img.setAttribute(STATE_ATTR, 'done')
		})
		.catch(err => {
			console.warn('[inline-files] immagine non caricata:', src, err && err.message)
			img.setAttribute(STATE_ATTR, 'error')
			if (!img.getAttribute('title')) {
				img.setAttribute('title', 'Immagine non disponibile')
			}
		})
}

function process(el) {
	// La entry nel registry esiste solo mentre la direttiva e' attiva sull'elemento:
	// creata qui, rimossa da revokeAll all'unbind.
	urlsFor(el)
	revokeDetached(el)
	el.querySelectorAll('img[src^="' + FILES_PREFIX + '"]').forEach(img => {
		if (img.hasAttribute(STATE_ATTR)) return
		resolveImage(el, img)
	})
}

Vue.directive('inline-files', {
	// bind: l'innerHTML di v-html e' gia' impostato, meglio neutralizzare i src prima
	// che l'elemento entri nel documento.
	bind: process,
	inserted: process,
	componentUpdated: process,
	unbind: revokeAll,
})
